import { useState } from 'react';
import { useApiQuery } from '../use-api.js';

interface VerdictDistribution {
  auto_approved?: number;
  human_required?: number;
  skipped?: number;
  [key: string]: number | undefined;
}

interface CostSummary {
  total_usd?: number;
  top_prs?: Array<{ org: string; repo: string; pr: number; total_usd: number }>;
  [key: string]: unknown;
}

interface DigestResponse {
  week: string;
  org: string;
  verdict_distribution?: VerdictDistribution;
  new_authors?: string[];
  cost_summary?: CostSummary;
}

function currentIsoWeekMinus1(): string {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  const jan1 = new Date(d.getFullYear(), 0, 1);
  const weekNum = Math.ceil(((d.getTime() - jan1.getTime()) / 86400000 + jan1.getDay() + 1) / 7);
  return `${d.getFullYear()}-${String(weekNum).padStart(2, '0')}`;
}

export function WeeklyDigest() {
  const [week, setWeek] = useState(currentIsoWeekMinus1());

  const { data, isLoading, error } = useApiQuery<DigestResponse>(
    ['digest', week],
    `/api/digest?week=${week}`
  );

  function fmtUsd(n: number | undefined): string {
    if (n === undefined || n === null) return '—';
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  return (
    <div>
      <h2 className="text-lg font-semibold mb-4">Weekly Digest</h2>

      <div className="flex gap-3 mb-6 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Week (YYYY-WW)</label>
          <input
            type="text"
            value={week}
            onChange={e => setWeek(e.target.value)}
            placeholder="2025-18"
            className="border rounded px-3 py-1.5 text-sm w-36"
          />
        </div>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {error && <p className="text-sm text-red-600">Error: {String(error)}</p>}

      {data && (
        <>
          <div className="bg-white border rounded-lg p-4 mb-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Summary</h3>
            <p className="text-sm text-gray-600">Week: <span className="font-medium">{data.week}</span></p>
            <p className="text-sm text-gray-600">Org: <span className="font-medium">{data.org}</span></p>
          </div>

          <div className="bg-white border rounded-lg p-4 mb-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Verdict Distribution</h3>
            {data.verdict_distribution ? (
              <table className="text-sm">
                <tbody className="divide-y divide-gray-100">
                  {Object.entries(data.verdict_distribution).map(([k, v]) => (
                    <tr key={k}>
                      <td className="py-1 pr-6 text-gray-500">{k}</td>
                      <td className="py-1 font-semibold">{v ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-gray-400">No distribution data.</p>
            )}
          </div>

          <div className="bg-white border rounded-lg p-4 mb-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">New Authors</h3>
            {(data.new_authors ?? []).length > 0 ? (
              <ul className="text-sm space-y-1">
                {data.new_authors!.map(a => (
                  <li key={a} className="font-mono text-xs">{a}</li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-400">None this week.</p>
            )}
          </div>

          <div className="bg-white border rounded-lg p-4 mb-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Cost Summary</h3>
            {data.cost_summary ? (
              <>
                <p className="text-sm mb-3">
                  Total: <span className="font-semibold">{fmtUsd(data.cost_summary.total_usd as number)}</span>
                </p>
                {(data.cost_summary.top_prs ?? []).length > 0 && (
                  <>
                    <p className="text-xs text-gray-500 mb-1">Top PRs</p>
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-left">
                        <tr>
                          <th className="px-3 py-1.5 text-xs font-medium text-gray-600">Org</th>
                          <th className="px-3 py-1.5 text-xs font-medium text-gray-600">Repo</th>
                          <th className="px-3 py-1.5 text-xs font-medium text-gray-600">PR</th>
                          <th className="px-3 py-1.5 text-xs font-medium text-gray-600 text-right">Cost</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {data.cost_summary.top_prs!.map(p => (
                          <tr key={`${p.org}/${p.repo}/${p.pr}`}>
                            <td className="px-3 py-1.5">{p.org}</td>
                            <td className="px-3 py-1.5 font-mono text-xs">{p.repo}</td>
                            <td className="px-3 py-1.5">#{p.pr}</td>
                            <td className="px-3 py-1.5 text-right">{fmtUsd(p.total_usd)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-400">No cost data.</p>
            )}
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800 font-medium">Action buttons coming in v2</p>
            <p className="text-xs text-yellow-700 mt-1">
              Dismiss / Suppress / Tweak prompt / Adjust tier rule actions require cluster records
              to be ingested and GitHub App auth for PR creation. Deferred from v1 scope.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
